import { animate, animateChild, group, query, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, ActivationStart, NavigationEnd, Router, RouterLink } from '@angular/router';
import { fade } from '@common/animations/fade';
import { ButtonComponent } from '@common/components/button/button.component';
import { InputType } from '@common/components/input/input.type';
import { SidebarProfileComponent } from '@common/components/sidebar-profile/sidebar-profile.component';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { User } from '@common/models/user';
import { WebsocketService } from '@common/services/websocket.service';
import { environment } from '@environments/environment';
import { NgIcon } from '@ng-icons/core';
import { tapSuccessResult } from '@ngneat/query';
import { TranslatePipe } from '@ngx-translate/core';
import { PanelBarComponent, PanelBarItemComponent, PanelBarItemTitleDirective } from '@progress/kendo-angular-layout';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { DarkModeService } from '@services/dark-mode.service';
import { DialogService } from '@services/dialog.service';
import { QueryService } from '@services/query.service';
import { RememberStateService } from '@services/remember-state.service';
import { SettingsService } from '@services/settings.service';
import { ToastrNotificationService } from '@services/toastr-notification.service';
import { UserService } from '@services/user.service';
import { NAVIGATION, NavItem } from 'app/navigation';
import { filter, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    animations: [
        trigger('openClose', [
            state(
                'open',
                style({
                    width: '16rem'
                })
            ),
            state(
                'closed',
                style({
                    width: '3rem'
                })
            ),
            transition('open => closed', [
                group([
                    query('@fade', [animateChild()]),
                    query('@alignLogo', [animateChild()]),
                    animate('0.3s ease-out')
                ])
            ]),
            transition('closed => open', [
                group([
                    query('@fade', [animateChild()]),
                    query('@alignLogo', [animateChild()]),
                    animate('0.3s ease-out')
                ])
            ])
        ]),
        fade,
        trigger('alignLogo', [
            state(
                'show',
                style({
                    marginLeft: '12px'
                })
            ),
            state(
                'hide',
                style({
                    marginLeft: '5px',
                    marginTop: '20px'
                })
            ),
            transition('show => hide', [animate('300ms ease-out')]),
            transition('hide => show', [animate('300ms ease-in')])
        ])
    ],
    imports: [
        NgClass,
        ButtonComponent,
        TooltipDirective,
        TranslatePipe,
        PanelBarComponent,
        PanelBarItemComponent,
        RouterLink,
        NgIcon,
        PanelBarItemTitleDirective,
        SidebarProfileComponent
    ]
})
export class SidebarComponent implements OnInit, OnDestroy {
    destroy$ = new Subject<boolean>();
    isSidebarExpanded: boolean;
    environmentTitle = environment.title;
    environmentLogo = environment.settings.header.logo;
    navigation = NAVIGATION;
    currentUser: User;
    private websocketService = inject(WebsocketService);
    private title = inject(Title);
    private rememberStateService = inject(RememberStateService);
    private userService = inject(UserService);
    private router = inject(Router);
    private toastrNotificationService = inject(ToastrNotificationService);
    private darkModeService = inject(DarkModeService);
    private dialogService = inject(DialogService);
    private queryService = inject(QueryService);
    private settingsService = inject(SettingsService);

    ngOnInit(): void {
        const sidebarPinned = this.rememberStateService.get<boolean>('sidebarPinned');
        if (sidebarPinned === undefined) this.setSidebarPinned(true);
        this.isSidebarExpanded = sidebarPinned ?? true;

        this.userService.currentUserSubject.subscribe((user) => {
            const isAuthenticated = this.userService.isAuthenticatedSubject.getValue();
            if (isAuthenticated) {
                this.currentUser = user;
                this.filterNavigationItems();
            }
        });

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.websocketService.info(`Navigated to ${event.url}`);
                const title = this.getDeepestTitle(this.router.routerState.snapshot.root);
                this.title.setTitle(title ? `${title} · ${environment.title}` : `${environment.title}`);
                // if there are not selected items, get the url and select the item

                this.activateNavItems(this.navigation);
            }
        });

        this.router.events
            .pipe(
                filter(
                    (event) =>
                        event instanceof ActivationStart &&
                        event.snapshot.data.permissions &&
                        (!Array.isArray(event.snapshot.data.permissions) || event.snapshot.data.permissions.length > 0)
                )
            )
            .subscribe((event) => {
                const { permissions } = (event as ActivationStart).snapshot.data;

                if (!this.currentUser.hasPermission(permissions)) {
                    console.dir(event);
                    console.error('Authorization failed.', this.currentUser, permissions);
                    this.websocketService.error(
                        `Authorization for ${this.getResolvedUrl((event as ActivationStart).snapshot)} failed.`
                    );
                    this.toastrNotificationService.show({ type: 'error', message: 'Unauthorized' });

                    this.router.navigate(['/401'], {
                        queryParams: { returnUrl: this.getResolvedUrl((event as ActivationStart).snapshot) }
                    });
                }
            });

        this.getSettings();
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    filterNavigationItems(): void {
        if (!this.currentUser) return;
        this.navigation = NAVIGATION.map((nav) => ({ ...nav })).filter((item: NavItem) => this.filterNavItem(item));
    }

    toggleExpanded() {
        this.isSidebarExpanded = !this.isSidebarExpanded;
        this.setSidebarPinned(this.isSidebarExpanded);
    }

    toggleTheme() {
        this.darkModeService.toggle();
    }

    toggleItemExpanded(clickedItem: NavItem) {
        const newNavigation = this.navigation.map((item) => {
            // Clicking on disabled menu item does nothing
            if (clickedItem.disabled) return item;
            // As addition to Kendo UI PanelBar logic:
            // Collapse item if childless item is clicked
            if (!clickedItem.children) item.expanded = false;
            // Expand item if item is clicked, colapse all other items
            item.expanded = item === clickedItem;
            return item;
        });
        this.navigation = newNavigation;
    }

    openUploadImageDialog() {
        this.dialogService
            .form({
                options: {
                    title: 'Upload background image',
                    titleIcons: ['faSolidUpload'],
                    properties: [
                        {
                            id: 'image',
                            label: 'Background image',
                            type: InputType.FILE,
                            restrictions: { allowedExtensions: ['jpg', 'png'] },
                            isRequired: true
                        }
                    ],
                    canConfirm: (model) => model.image
                }
            })
            .then((data: any) => {
                if (!data) return;
                this.queryService
                    .command('SaveBackgroundImage', {
                        content: data.image.content,
                        name: data.image.name
                    })
                    .then((res: any) => {
                        if (!res) return;
                        // Maybe write a separate query to get the image only, not all settings
                        this.queryService.queryClient.invalidateQueries();
                        this.toastrNotificationService.show({
                            type: 'success',
                            message: 'Background image uploaded successfully'
                        });
                    });
            });
    }

    isDarkTheme() {
        return this.darkModeService.get();
    }

    private filterNavItem(navItem: NavItem): boolean {
        if (!this.canUserAccessNavItem(navItem) || navItem.hidden) return false;

        if (navItem.children) navItem.children = navItem.children.filter((child) => this.filterNavItem(child));

        return true;
    }

    private canUserAccessNavItem(navItem: NavItem): boolean {
        if (!navItem.permissions || this.currentUser.isSystemUser) return true;

        const permissions = Array.isArray(navItem.permissions) ? navItem.permissions : [navItem.permissions];
        return this.currentUser.hasPermission(...permissions);
    }

    private getDeepestTitle(routeSnapshot: ActivatedRouteSnapshot) {
        let title = routeSnapshot.data ? routeSnapshot.data.title : '';

        if (routeSnapshot.firstChild) {
            title = this.getDeepestTitle(routeSnapshot.firstChild) || title;
        }

        return title;
    }

    private getResolvedUrl(route: ActivatedRouteSnapshot): string {
        return route.pathFromRoot.map((v) => v.url.map((segment) => segment.toString()).join('/')).join('/');
    }

    private activateNavItems(data: NavItem[]) {
        data.forEach((item) => {
            if (item.children?.length > 0) {
                item.children.forEach((child) => {
                    if (
                        this.router.isActive(child.path, {
                            matrixParams: 'ignored',
                            queryParams: 'ignored',
                            paths: 'subset',
                            fragment: 'ignored'
                        })
                    ) {
                        item.expanded = true;
                        child.selected = true;
                    } else {
                        child.selected = false;
                    }
                });
            } else {
                item.selected = this.router.isActive(item.path, {
                    matrixParams: 'ignored',
                    queryParams: 'ignored',
                    paths: 'exact',
                    fragment: 'ignored'
                });
            }
        });
    }

    private setSidebarPinned(value: boolean) {
        this.rememberStateService.set('sidebarPinned', value);
    }

    private getSettings() {
        this.settingsService
            .getSettings()
            .pipe(
                takeUntil(this.destroy$),
                tapSuccessResult((result: any) => {
                    if (!result) return;
                    const title = result.find(
                        (setting: Setting) => setting.key === SettingKeyCodes.LongCompanyName
                    )?.value;
                    if (title) this.environmentTitle = title;
                    const companyLogo = result.find((setting: Setting) => setting.key === SettingKeyCodes.Logo);
                    if (companyLogo?.value) this.handleLogo(companyLogo.value);
                    else this.environmentLogo = environment.settings.header.logo;
                })
            )
            .subscribe();
    }

    private handleLogo(imageContent: string) {
        const decodedData = atob(imageContent);
        const uint8Array = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; ++i) {
            uint8Array[i] = decodedData.charCodeAt(i);
        }
        const blob = new Blob([uint8Array]);
        this.environmentLogo = URL.createObjectURL(blob);
    }
}
